// 智能体：列表（能不能用＝有没有配模型）、新建、编辑、删掉自己建的。
// 服务端更新时整份替换 config：编辑时先取完整的智能体，只改界面上的几项，其余原样送回。

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App as AntApp } from 'antd'
import { Bot, ChevronRight, Plus } from 'lucide-react'
import { createAgent, deleteAgent, getAgent, updateAgent } from '../../api/agents'
import { modelLabel } from '../../api/settings'
import { listAgents, listChatModels } from '../../api/chat'
import type { Agent } from '../../api/types'
import { Pill, Segmented } from '../../ui'
import { useKnowledgeBases } from '../chat/Composer'
import '../kb/dialogs.css'
import '../kb/kb.css'
import './agents.css'

const MODE_LABEL: Record<string, string> = { 'quick-answer': '快速问答', 'smart-reasoning': '智能推理' }
const scopeText = (a: Agent, kbs: { id: string; name: string }[]) => {
  const m = a.config?.kb_selection_mode
  if (m === 'none') return '不查知识库'
  if (m === 'selected') {
    const names = (a.config?.knowledge_bases ?? []).map((id) => kbs.find((k) => k.id === id)?.name).filter(Boolean)
    return names.length ? `查：${names.join('、')}` : '查：（没选知识库）'
  }
  return '查全部知识库'
}

export function AgentsPage() {
  const agents = useQuery({ queryKey: ['agents'], queryFn: listAgents })
  const models = useQuery({ queryKey: ['chat-models'], queryFn: listChatModels, staleTime: 5 * 60_000 }).data ?? []
  const kbs = useKnowledgeBases().data ?? []
  const list = agents.data ?? []
  return (
    <div className="page">
      <div className="page-h">
        <h1>智能体</h1>
        <span className="grow" />
        <Link to="/agents/new" className="sk-pill" data-variant="primary" data-size="md">
          <Plus width={16} height={16} />
          新建智能体
        </Link>
      </div>
      <p className="page-lead">对话时「回答方式」里能选的就是这些。没配模型的不能用，配上就行。</p>
      {agents.isError && <p className="empty">没能载入智能体：{agents.error.message}</p>}
      <div className="agent-grid" aria-busy={agents.isPending}>
        {list.map((a) => {
          const model = models.find((m) => m.id === a.config?.model_id)
          return (
            <Link key={a.id} to={`/agents/${a.id}`} className="sk-card agent-card" data-interactive="true" aria-label={a.name}>
              <span className="agent-top">
                <span className="kb-cover" aria-hidden="true">
                  <Bot />
                </span>
                <span className="sk-card-title">{a.name}</span>
                {a.is_builtin && <span className="sk-chip">内置</span>}
              </span>
              {a.description && <span className="sk-card-body">{a.description}</span>}
              <span className="sk-card-meta agent-meta">
                <span className="sk-chip">{MODE_LABEL[a.config?.agent_mode ?? ''] ?? a.config?.agent_mode}</span>
                <span>{scopeText(a, kbs)}</span>
              </span>
              <span className="agent-model" data-ok={!!a.config?.model_id}>
                {a.config?.model_id ? `模型：${model ? modelLabel(models, model) : a.config.model_id}` : '没配模型，现在不能用'}
              </span>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

interface Form {
  name: string
  description: string
  mode: string
  model: string
  scope: 'all' | 'selected' | 'none'
  kbs: string[]
  prompt: string
}

const formOf = (a?: Agent): Form => ({
  name: a?.name ?? '',
  description: a?.description ?? '',
  mode: a?.config?.agent_mode ?? 'quick-answer',
  model: a?.config?.model_id ?? '',
  scope: (['all', 'selected', 'none'].includes(a?.config?.kb_selection_mode ?? '') ? a!.config!.kb_selection_mode : 'all') as Form['scope'],
  kbs: a?.config?.knowledge_bases ?? [],
  prompt: typeof a?.config?.system_prompt === 'string' ? a.config.system_prompt : '',
})

export function AgentEditPage() {
  const { agentId = 'new' } = useParams()
  const isNew = agentId === 'new'
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message, modal } = AntApp.useApp()
  const agent = useQuery({ queryKey: ['agent', agentId], enabled: !isNew, queryFn: () => getAgent(agentId) })
  const models = useQuery({ queryKey: ['chat-models'], queryFn: listChatModels, staleTime: 5 * 60_000 }).data ?? []
  const kbs = (useKnowledgeBases().data ?? []).filter((k) => k.type !== 'faq')
  const [form, setForm] = useState<Form>(formOf())
  useEffect(() => {
    if (agent.data) setForm(formOf(agent.data))
  }, [agent.data])
  useEffect(() => {
    if (isNew && !form.model && models.length) setForm((f) => ({ ...f, model: (models.find((m) => m.is_default) ?? models[0]).id }))
  }, [isNew, models, form.model])
  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }))
  const builtin = !!agent.data?.is_builtin

  const save = useMutation({
    mutationFn: () => {
      // 只改界面上这几项，其余配置原样带回（服务端会整份替换）
      const config = {
        ...(agent.data?.config ?? {}),
        agent_mode: form.mode,
        model_id: form.model,
        kb_selection_mode: form.scope,
        knowledge_bases: form.scope === 'selected' ? form.kbs : [],
        system_prompt: form.prompt,
      }
      return isNew ? createAgent({ name: form.name.trim(), description: form.description.trim(), config }) : updateAgent(agentId, { name: form.name.trim(), description: form.description.trim(), avatar: agent.data?.avatar, config })
    },
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: ['agents'] })
      qc.setQueryData(['agent', a.id], a)
      message.success(isNew ? `建好了「${a.name}」` : '改好了')
      navigate('/agents')
    },
    onError: (e) => void message.error(e.message),
  })
  const remove = useMutation({
    mutationFn: () => deleteAgent(agentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents'] })
      message.success('删掉了')
      navigate('/agents', { replace: true })
    },
    onError: (e) => void message.error(e.message),
  })

  if (!isNew && agent.isPending) return <div className="page" aria-busy="true" />
  if (!isNew && agent.isError) return <p className="empty-chat">没能打开这个智能体：{agent.error.message}</p>
  const title = isNew ? '新建智能体' : agent.data!.name
  const problem = !form.name.trim() ? '要有名字' : form.scope === 'selected' && !form.kbs.length ? '选至少一个知识库' : null

  return (
    <div className="page agent-edit">
      <div className="crumb">
        <Link to="/agents">智能体</Link>
        <ChevronRight width={13} height={13} />
        <span>{title}</span>
      </div>
      <h1>{title}</h1>
      <form
        className="dlg"
        onSubmit={(e) => {
          e.preventDefault()
          if (!problem) save.mutate()
        }}
      >
        <label htmlFor="ag-name">名字</label>
        <input id="ag-name" value={form.name} maxLength={60} onChange={(e) => set({ name: e.target.value })} readOnly={builtin} />
        <label htmlFor="ag-desc">说明</label>
        <textarea id="ag-desc" rows={2} value={form.description} maxLength={300} onChange={(e) => set({ description: e.target.value })} readOnly={builtin} />
        {builtin && <p className="field-note">内置智能体的名字和说明改不了，下面的设置可以改。</p>}

        <label>回答方式</label>
        <Segmented
          label="回答方式"
          value={form.mode}
          onChange={(mode) => set({ mode })}
          items={[
            { value: 'quick-answer', label: '快速问答' },
            { value: 'smart-reasoning', label: '智能推理' },
          ]}
        />
        <p className="field-note">{form.mode === 'quick-answer' ? '查资料、直接回答，快。' : '会多步查资料、想一想再回答，慢一些，适合复杂问题。'}</p>

        <label htmlFor="ag-model">模型</label>
        <select id="ag-model" value={form.model} onChange={(e) => set({ model: e.target.value })}>
          <option value="">（不配：这个智能体就不能用）</option>
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {modelLabel(models, m)}
              {m.is_default ? '（默认）' : ''}
            </option>
          ))}
        </select>

        <label>查哪些知识库</label>
        <Segmented
          label="查哪些知识库"
          value={form.scope}
          onChange={(scope) => set({ scope })}
          items={[
            { value: 'all', label: '全部' },
            { value: 'selected', label: '指定的' },
            { value: 'none', label: '不查' },
          ]}
        />
        {form.scope === 'selected' && (
          <div className="kb-checks" role="group" aria-label="指定的知识库">
            {kbs.map((k) => (
              <label key={k.id} className="kb-check">
                <input type="checkbox" checked={form.kbs.includes(k.id)} onChange={(e) => set({ kbs: e.target.checked ? [...form.kbs, k.id] : form.kbs.filter((x) => x !== k.id) })} />
                {k.name}
              </label>
            ))}
          </div>
        )}

        <label htmlFor="ag-prompt">提示词（可选）</label>
        <textarea id="ag-prompt" rows={8} value={form.prompt} maxLength={20000} placeholder="告诉它是谁、怎么回答。留空用默认的。" onChange={(e) => set({ prompt: e.target.value })} />

        <div className="dlg-acts">
          <Pill type="submit" variant="primary" disabled={!!problem || save.isPending} title={problem ?? undefined}>
            {isNew ? '新建' : '保存'}
          </Pill>
          <Pill onClick={() => navigate('/agents')}>取消</Pill>
          <span className="grow" />
          {!isNew && !builtin && (
            <Pill
              className="danger"
              onClick={() =>
                modal.confirm({
                  title: `删掉「${agent.data!.name}」？`,
                  content: '对话里已经用它回答过的不受影响。',
                  okText: '删掉',
                  cancelText: '不删',
                  okButtonProps: { danger: true },
                  onOk: () => remove.mutateAsync(),
                })
              }
            >
              删除
            </Pill>
          )}
        </div>
        {problem && <p className="field-note">{problem}</p>}
      </form>
    </div>
  )
}
